import { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Button,
  Alert,
  AlertIcon,
  useToast,
  FormErrorMessage,
  Select,
  Spinner,
  Text,
  Tooltip
} from '@chakra-ui/react';

const AdminCRUConductor = () => {
  const { id } = useParams(); // Obtenemos el ID de la URL
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isReadOnly = searchParams.get('mode') === 'read';
  const isEditing = Boolean(id); // true si hay un ID, false si no
  const isAdminUser = user?.user_type === 'administrador';

  const toast = useToast();

  // Estado inicial del formulario. La contraseña se deja vacía intencionadamente.
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    habilitado: true,
    activo: false,
    acepto_politicas: false,
    forma_pago: 'Bizum',
    matricula_vehiculo: '',
    licencia_conducir: '',
    cargo: 'conductor',
    cif: '',
    tipo_ecotaxi: '',
    sillita_bebe: false,
    apto_minusvalidos: false,
    telefono2: '',
    direccion: '',
    idioma: 'es',
    comentarios: '',
    declaracion_responsable: false,
    acepto_normativa_over: false,
    credito: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(isEditing); // true si estamos editando
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // --- useEffect para cargar datos del conductor si estamos en modo edición ---
  useEffect(() => {
    if (isEditing) {
      const fetchConductor = async () => {
        setLoadingInitialData(true); // Asegurarse de que el spinner se muestre
        try {
          const baseURL = process.env.REACT_APP_API_BASE_URL;
          const token = localStorage.getItem('auth-token');
          const response = await fetch(`${baseURL}/api/conductores/${id}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              `Error al cargar conductor: ${response.status} - ${
                errorData?.detail || response.statusText
              }`
            );
          }
          const data = await response.json();

          // Actualizamos formData con los datos del conductor
          setFormData({
            ...formData, // Mantiene valores por defecto si no están en 'data' (aunque en PUT suelen venir todos)
            ...data,
            password: '', // No precargamos la contraseña por seguridad
            // Asegúrate de que los booleanos se manejen correctamente si vienen como 0/1 del backend
            habilitado: data.habilitado ?? true,
            activo: Boolean(data.activo) ?? false,
            acepto_politicas: Boolean(data.acepto_politicas),
            sillita_bebe: Boolean(data.sillita_bebe),
            apto_minusvalidos: Boolean(data.apto_minusvalidos),
            declaracion_responsable: Boolean(data.declaracion_responsable),
            acepto_normativa_over: Boolean(data.acepto_normativa_over),
            credito: data.credito ?? 0,
          });
        } catch (err) {
          setError(err.message);
          toast({
            title: 'Error al cargar conductor',
            description: err.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          // Si hay un error al cargar, probablemente queramos redirigir o mostrar un error persistente
          // navigate('/admin/conductores'); // Opcional: redirigir si el conductor no se encuentra
        } finally {
          setLoadingInitialData(false);
        }
      };
      fetchConductor();
    } else {
      // Si no estamos editando (creación), resetear el formulario a su estado inicial
      setFormData({
        email: '',
        password: '',
        nombre: '',
        apellidos: '',
        telefono: '',
        habilitado: true,
        activo: false,
        acepto_politicas: false,
        forma_pago: 'Bizum',
        matricula_vehiculo: '',
        licencia_conducir: '',
        cargo: 'conductor',
        cif: '',
        tipo_ecotaxi: '',
        sillita_bebe: false,
        apto_minusvalidos: false,
        telefono2: '',
        direccion: '',
        idioma: 'es',
        comentarios: '',
        declaracion_responsable: false,
        acepto_normativa_over: false,
        credito: 0,
      });
      setPasswordError(''); // Limpiar errores de contraseña
      setLoadingInitialData(false); // No hay datos iniciales que cargar
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]); // Dependencias: id y isEditing (cambio de ruta)


  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevFormData) => { // Usar función de actualización para evitar problemas de cierre
      const newFormData = {
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'password') {
        validatePassword(newFormData.password); // Validar la nueva contraseña inmediatamente
      }
      return newFormData;
    });
  };

  const validatePassword = (password) => {
    // Si estamos editando y el campo de contraseña está vacío,
    // significa que el usuario no quiere cambiarla, por lo que no la validamos como requerida.
    if (isEditing && password === '') {
      setPasswordError('');
      return true;
    }

    // Validación normal de la contraseña
    let currentPasswordError = '';
    if (password.length < 8) {
      currentPasswordError = 'La contraseña debe tener al menos 8 caracteres.';
    } else if (!/[a-z]/.test(password)) {
      currentPasswordError = 'La contraseña debe contener al menos una minúscula.';
    } else if (!/[A-Z]/.test(password)) {
      currentPasswordError = 'La contraseña debe contener al menos una mayúscula.';
    } else if (!/[0-9]/.test(password)) {
      currentPasswordError = 'La contraseña debe contener al menos un número.';
    }

    setPasswordError(currentPasswordError);
    return currentPasswordError === '';
  };


  // --- Validación del formulario (se ejecuta cuando formData o passwordError cambian) ---
  useEffect(() => {
    const isEmailValid = formData.email !== '';
    const isNombreValid = formData.nombre !== '';
    const isTelefonoValid = formData.telefono !== '';
    const isMatriculaValid = formData.matricula_vehiculo.length >= 6 && formData.matricula_vehiculo.length <= 20;
    const isLicenciaValid = formData.licencia_conducir.length >= 1 && formData.licencia_conducir.length <= 50;
    const isCargoValid = formData.cargo === 'titular' || formData.cargo === 'conductor';
    const isCifValid = formData.cif.length >= 8 && formData.cif.length <= 15;
    const isPoliticasAceptadas = formData.acepto_politicas;
    const isDeclaracionResponsable = formData.declaracion_responsable;
    const isNormativaOverAceptada = formData.acepto_normativa_over;
    const isTipoEcotaxiValid = formData.tipo_ecotaxi !== '';

    // Lógica para la contraseña:
    // En modo creación, la contraseña debe ser no vacía y sin errores.
    // En modo edición, la contraseña puede estar vacía (no se cambia), o si se ha introducido, debe ser válida.
    const isPasswordValidCondition = isEditing
        ? (formData.password === '' || validatePassword(formData.password))
        : (formData.password !== '' && validatePassword(formData.password));

    // Construir mensaje de validación
    const missingFields = [];
    if (!isEmailValid) missingFields.push('Email');
    if (!isPasswordValidCondition) {
      if (isEditing && formData.password !== '' && !validatePassword(formData.password)) {
        missingFields.push('Contraseña válida');
      } else if (!isEditing && formData.password === '') {
        missingFields.push('Contraseña');
      } else if (!isEditing && !validatePassword(formData.password)) {
        missingFields.push('Contraseña válida');
      }
    }
    if (!isNombreValid) missingFields.push('Nombre');
    if (!isTelefonoValid) missingFields.push('Teléfono');
    if (!isMatriculaValid) missingFields.push('Matrícula (6-20 caracteres)');
    if (!isLicenciaValid) missingFields.push('Número de licencia de taxi (1-50 caracteres)');
    if (!isCargoValid) missingFields.push('Cargo válido');
    if (!isCifValid) missingFields.push('CIF (8-15 caracteres)');
    if (!isPoliticasAceptadas) missingFields.push('Aceptación de políticas');
    if (!isDeclaracionResponsable) missingFields.push('Declaración responsable');
    if (!isNormativaOverAceptada) missingFields.push('Aceptación normativa OVER');
    if (!isTipoEcotaxiValid) missingFields.push('Tipo de ecotaxi');

    if (missingFields.length > 0) {
      setValidationMessage(`Faltan campos: ${missingFields.join(', ')}`);
    } else {
      setValidationMessage('');
    }

    setIsFormValid(
      isEmailValid &&
      isPasswordValidCondition && // Usamos la condición de password específica
      isNombreValid &&
      isTelefonoValid &&
      isMatriculaValid &&
      isLicenciaValid &&
      isCargoValid &&
      isCifValid &&
      isPoliticasAceptadas &&
      isDeclaracionResponsable &&
      isNormativaOverAceptada &&
      isTipoEcotaxiValid
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, passwordError, isEditing]); // Añadimos isEditing a las dependencias


  // --- Manejo del envío del formulario ---
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Re-validar la contraseña justo antes de enviar, en caso de que el usuario la borrara después de validarla
    const finalPasswordValidation = validatePassword(formData.password);
    if (!isFormValid || !finalPasswordValidation) { // Asegurarse de que la validación final se cumpla
        toast({
            title: 'Formulario incompleto o inválido',
            description: 'Por favor, revisa todos los campos requeridos y errores de contraseña.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
        });
        return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const url = isEditing ? `${baseURL}/api/conductores/${id}` : `${baseURL}/api/conductores/`;
      const method = isEditing ? 'PUT' : 'POST';

      // Prepara los datos a enviar.
      // Si estamos editando y la contraseña está vacía, no la enviamos al backend.
      const dataToSend = { ...formData };
      if (isEditing && dataToSend.password === '') {
        delete dataToSend.password; // Eliminar la clave 'password' si está vacía en edición
      }

      if (!isAdminUser) {
        delete dataToSend.credito;
      }

      // Eliminar campos que no deben ser enviados al backend (solo lectura o gestionados por el servidor)
      const fieldsToExclude = ['id', 'fecha_creacion', 'ultimo_login', 'fecha_cambio_datos', 'fecha_cambio_password', 'hashed_password'];
      fieldsToExclude.forEach(field => delete dataToSend[field]);

      const token = localStorage.getItem('auth-token');
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        toast({
          title: isEditing ? 'Conductor actualizado' : 'Conductor creado',
          description: isEditing
            ? 'Los datos del conductor han sido actualizados exitosamente.'
            : 'El conductor ha sido registrado exitosamente.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Redirigir a la lista de conductores después de una operación exitosa
        navigate('/admin/conductores');
      } else {
        const errorData = await response.json();
        setError(
          `Error al ${isEditing ? 'actualizar' : 'crear'} conductor: ${
            response.status
          } - ${errorData?.detail || response.statusText}`
        );
      }
    } catch (err) {
      setError(`Error de conexión: ${err.message}`);
      toast({
        title: 'Error de conexión',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingInitialData) {
    return (
      <Box p={4} textAlign="center" className="admin-loading">
        <Spinner size="xl" />
        <Text mt={4} className="admin-loading-text">Cargando datos del conductor...</Text>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" overflow="hidden" maxWidth="xl" mx="auto" className="admin-page-container">
      <Heading mb={4} className="admin-page-title">
        {isReadOnly ? `${t('admin_conductor_titulo_detalles')}: ${formData.nombre} ${formData.apellidos}` : isEditing && formData.nombre ? `${t('admin_conductor_titulo_editar')}: ${formData.nombre} ${formData.apellidos}` : t('admin_conductor_titulo_crear')}
      </Heading>
      {error && (
        <Alert status="error" mb={4} className="admin-alert admin-alert-error">
          <AlertIcon />
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit} className="admin-form-container">
        <Box display="grid" gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="email" className="admin-form-label">{t('admin_campo_email')}</FormLabel>
            <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          {/* El campo de contraseña es opcional en edición */}
          {!isReadOnly && (
            <FormControl mb={4} isRequired={!isEditing} isInvalid={!!passwordError}>
              <FormLabel htmlFor="password" className="admin-form-label">{t('admin_campo_password')} {isEditing && t('admin_conductor_password_hint')}</FormLabel>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isEditing ? t('admin_conductor_password_placeholder') : ''}
                className="admin-form-input"
                bg="white"
                color="black"
              />
              {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
            </FormControl>
          )}

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="nombre" className="admin-form-label">{t('admin_campo_nombre')}</FormLabel>
            <Input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel htmlFor="apellidos" className="admin-form-label">{t('admin_campo_apellidos')}</FormLabel>
            <Input type="text" id="apellidos" name="apellidos" value={formData.apellidos} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="telefono" className="admin-form-label">{t('admin_campo_telefono')}</FormLabel>
            <PhoneInput
              country={'es'}
              value={formData.telefono}
              onChange={phone => setFormData(prev => ({ ...prev, telefono: '+' + phone }))}
              inputProps={{
                name: 'telefono',
                required: true,
                autoFocus: false,
                disabled: isReadOnly
              }}
              disabled={isReadOnly}
              inputStyle={{
                width: '100%',
                background: '#fff',
                color: '#000', // Letras en negro
              }}
              specialLabel=""
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel htmlFor="telefono2" className="admin-form-label">{t('admin_campo_telefono2')}</FormLabel>
            <PhoneInput
              country={'es'}
              value={formData.telefono2}
              onChange={phone => setFormData(prev => ({ ...prev, telefono2: '+' + phone }))}
              inputProps={{
                name: 'telefono2',
                autoFocus: false,
                disabled: isReadOnly
              }}
              disabled={isReadOnly}
              inputStyle={{
                width: '100%',
                background: '#fff',
                color: '#000',
              }}
              specialLabel=""
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel htmlFor="direccion" className="admin-form-label">{t('admin_campo_direccion')}</FormLabel>
            <Input type="text" id="direccion" name="direccion" value={formData.direccion} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="matricula_vehiculo" className="admin-form-label">Matrícula del Vehículo</FormLabel>
            <Input type="text" id="matricula_vehiculo" name="matricula_vehiculo" value={formData.matricula_vehiculo} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="licencia_conducir" className="admin-form-label">{t('admin_campo_licencia')}</FormLabel>
            <Input type="text" id="licencia_conducir" name="licencia_conducir" value={formData.licencia_conducir} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="cargo" className="admin-form-label">Cargo</FormLabel>
            <Select id="cargo" name="cargo" value={formData.cargo} onChange={handleChange} bg="white" color="black" sx={{ option: { bg: 'white', color: 'black' } }} isDisabled={isReadOnly}>
              <option value="titular">Titular</option>
              <option value="conductor">Conductor</option>
            </Select>
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="cif" className="admin-form-label">{t('admin_campo_cif')}</FormLabel>
            <Input type="text" id="cif" name="cif" value={formData.cif} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="tipo_ecotaxi" className="admin-form-label">{t('admin_campo_tipo_ecotaxi')}</FormLabel>
            <Select
              id="tipo_ecotaxi"
              name="tipo_ecotaxi"
              value={formData.tipo_ecotaxi}
              onChange={handleChange}
              placeholder="Selecciona un tipo"
              bg="white"
              color="black"
              sx={{ option: { bg: 'white', color: 'black' } }}
              isDisabled={isReadOnly}
            >
              <option value="híbrido">Híbrido</option>
              <option value="híbrido enchufable">Híbrido Enchufable</option>
              <option value="GNC/GLP">GNC/GLP</option>
              <option value="eléctrico">Eléctrico</option>
              <option value="hidrógeno">Hidrógeno</option>
            </Select>
          </FormControl>

          <FormControl display="flex" alignItems="center" mb={4}>
            <FormLabel htmlFor="sillita_bebe" mb="0" mr={2} className="admin-form-label">
              {t('admin_campo_sillita_bebe')}
            </FormLabel>
            <Checkbox id="sillita_bebe" name="sillita_bebe" isChecked={formData.sillita_bebe} onChange={handleChange} isReadOnly={isReadOnly} isDisabled={isReadOnly} />
          </FormControl>

          <FormControl display="flex" alignItems="center" mb={4}>
            <FormLabel htmlFor="apto_minusvalidos" mb="0" mr={2} className="admin-form-label">
              {t('admin_campo_apto_minusvalidos')}
            </FormLabel>
            <Checkbox id="apto_minusvalidos" name="apto_minusvalidos" isChecked={formData.apto_minusvalidos} onChange={handleChange} isReadOnly={isReadOnly} isDisabled={isReadOnly} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel htmlFor="idioma" className="admin-form-label">{t('admin_campo_idioma')}</FormLabel>
            <Select id="idioma" name="idioma" value={formData.idioma} onChange={handleChange} bg="white" color="black" sx={{ option: { bg: 'white', color: 'black' } }} isDisabled={isReadOnly}>
              <option value="es">Español</option>
              <option value="en">Inglés</option>
              <option value="fr">Francés</option>
              <option value="de">Alemán</option>
            </Select>
          </FormControl>

          <FormControl mb={4}>
            <FormLabel htmlFor="comentarios" className="admin-form-label">{t('admin_campo_comentarios')}</FormLabel>
            <Input type="text" id="comentarios" name="comentarios" value={formData.comentarios} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
          </FormControl>

          <FormControl display="flex" alignItems="center" mb={4} isRequired>
            <FormLabel htmlFor="acepto_politicas" mb="0" mr={2} className="admin-form-label">
              {t('admin_campo_acepto_politicas')}
            </FormLabel>
            <Checkbox
              id="acepto_politicas"
              name="acepto_politicas"
              isChecked={formData.acepto_politicas}
              onChange={handleChange}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
            />
          </FormControl>

          <FormControl display="flex" alignItems="center" mb={4} isRequired>
            <FormLabel htmlFor="declaracion_responsable" mb="0" mr={2} className="admin-form-label">
              {t('admin_campo_declaracion_responsable')}
            </FormLabel>
            <Checkbox
              id="declaracion_responsable"
              name="declaracion_responsable"
              isChecked={formData.declaracion_responsable}
              onChange={handleChange}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
            />
          </FormControl>

          <FormControl display="flex" alignItems="center" mb={4} isRequired>
            <FormLabel htmlFor="acepto_normativa_over" mb="0" mr={2} className="admin-form-label">
              {t('admin_campo_acepto_normativa_over')}
            </FormLabel>
            <Checkbox
              id="acepto_normativa_over"
              name="acepto_normativa_over"
              isChecked={formData.acepto_normativa_over}
              onChange={handleChange}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
            />
          </FormControl>

          {/* Campo para 'habilitado'. Se muestra siempre, y por defecto true. */}
          <FormControl display="flex" alignItems="center" mb={4}>
            <FormLabel htmlFor="habilitado" mb="0" mr={2} className="admin-form-label">
              {t('admin_campo_habilitado')}
            </FormLabel>
            <Checkbox id="habilitado" name="habilitado" isChecked={formData.habilitado} onChange={handleChange} isReadOnly={isReadOnly} isDisabled={isReadOnly} />
          </FormControl>

          {/* Campo Crédito - Solo visible para administradores */}
          {isAdminUser && (
            <FormControl mb={4}>
              <FormLabel htmlFor="credito" className="admin-form-label">
                Crédito (puntos)
              </FormLabel>
              <Input
                type="number"
                id="credito"
                name="credito"
                value={formData.credito}
                onChange={handleChange}
                step="0.01"
                isReadOnly={isReadOnly}
                className="admin-form-input"
                bg="white"
                color="black"
              />
            </FormControl>
          )}

        </Box>

        {!isReadOnly && (
          <>
            <Tooltip label={validationMessage} isDisabled={isFormValid} hasArrow>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isSubmitting}
                isDisabled={!isFormValid || isSubmitting}
                mt={4}
                className="admin-button-primary"
              >
                {isEditing ? t('admin_boton_actualizar_conductor') : t('admin_boton_crear_conductor')}
              </Button>
            </Tooltip>
            <Button ml={2} mt={4} onClick={() => navigate('/admin/conductores')} className="admin-button-secondary">{t('admin_boton_cancelar')}</Button>
          </>
        )}
        {isReadOnly && user?.user_type === 'administrador' && (
          <Button mt={4} onClick={() => navigate('/admin/reservas')} className="admin-button-secondary">{t('admin_boton_volver_reservas')}</Button>
        )}
      </form>
    </Box>
  );
};

export default AdminCRUConductor;